import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiRefreshCw, FiX } from 'react-icons/fi';
import './PreviewPanel.css';
import { useMessages } from '../../../../i18n/I18n';

// iPhone 14 Pro screen (393×852) plus the 12px bezel, see .phone-bezel
const PHONE_WIDTH = 417;
const PHONE_HEIGHT = 876;

interface PreviewPanelProps {
  previewUrl: string;
  festivalId: string;
  version: number;
  // Bumped after each save of the festival data
  refreshKey: number;
  hasUnsavedChanges: boolean;
  onClose: () => void;
}

// Handled by the web client (FestiKit-Client FestivalSystems-Web): re-fetches the version without reloading
const REFRESH_MESSAGE = { type: 'festikit-preview:refresh' };

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  previewUrl,
  festivalId,
  version,
  refreshKey,
  hasUnsavedChanges,
  onClose,
}) => {
  const t = useMessages('editor');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const lastRefreshKey = useRef(refreshKey);
  const [scale, setScale] = useState(1);
  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
  const iframeSrc = `${previewUrl}/app.html?festivalId=${festivalId}&apiUrl=${encodeURIComponent(apiUrl)}&version=${version}`;

  const requestRefresh = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(REFRESH_MESSAGE, new URL(previewUrl).origin);
  }, [previewUrl]);

  useEffect(() => {
    if (refreshKey === lastRefreshKey.current) return;
    lastRefreshKey.current = refreshKey;
    requestRefresh();
  }, [refreshKey, requestRefresh]);

  // Scale the phone down instead of resizing it, so the app lays out at a real device width
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setScale(Math.min(1, width / PHONE_WIDTH, height / PHONE_HEIGHT));
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return (
    <aside className="preview-panel">
      <div className="preview-panel-toolbar">
        <span className="preview-panel-title">{t.previewTitle(version)}</span>
        <button type="button" className="btn btn-ghost" onClick={requestRefresh} title={t.refreshHint}>
          <FiRefreshCw aria-hidden="true" />
          {t.refresh}
        </button>
        <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label={t.closePreview}>
          <FiX aria-hidden="true" />
        </button>
      </div>
      {hasUnsavedChanges && (
        <p className="preview-panel-notice">
          <FiAlertCircle aria-hidden="true" />
          {t.previewUnsaved}
        </p>
      )}
      <div className="preview-panel-stage" ref={stageRef}>
        <div className="phone-bezel" style={{ transform: `scale(${scale})` }}>
          <div className="phone-screen">
            {/* clipboard-write: the web app copies share links (it has no share sheet) */}
            <iframe ref={iframeRef} src={iframeSrc} title={t.appPreview} allow="clipboard-write" />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default PreviewPanel;
